import type { PayrollOpPackageId } from '@/mocks/db/payrollOpPackagesCatalog';
import type { SalaryRecordStatus } from '@/mocks/db/types';

/** 一覧の既定ページサイズ。URL の不正値からのフォールバック先も兼ねる。 */
export const DEFAULT_PAGE_SIZE = 50;

/** 業務の系統。フィルタタブ・絞り込み・URL スキーマはここを唯一のソースにする */
export const OPERATION_KINDS = ['event', 'package'] as const;

/**
 * フィルタタブの値。`all` は業務の系統で絞らない。
 *
 * `OPERATION_KINDS` から導出する。3 箇所にリテラルを書くと、系統を増やしたときに
 * URL スキーマだけ狭いまま残る。zod 側の型のほうが狭いので代入は通ってしまい、
 * `.catch('all')` が実行時も黙って握り潰すため「新しいタブを押しても何も絞られない」
 * という気づきにくい壊れ方をする。
 */
export const OPERATION_KIND_FILTERS = ['all', ...OPERATION_KINDS] as const;
export type RecordOperationKindFilter = (typeof OPERATION_KIND_FILTERS)[number];

/** 業務イベントインスタンス。詳細は `/payroll-core-mock/operation-events/$eventId` */
export interface RecordOperationEventRef {
  kind: 'event';
  /** payrollGantt seed の `eventId`。そのままパスパラメータに渡せる */
  id: string;
  /**
   * payrollGantt seed の行が持つ `titleKey`。表示名は payroll-core の
   * `eventTitleSelectors`（titleKey 引き）から取るので、seed と同じキーで持つ。
   */
  titleKey: string;
}

/** 業務パッケージインスタンス。インスタンス詳細ルートは無く、index + search で開く */
export interface RecordOperationPackageRef {
  kind: 'package';
  /** `/payroll-core-mock/operation-packages` の `operationPackageId` */
  id: PayrollOpPackageId;
  /** 同 `operationPackagePatternId`。全パッケージが default パターン 1 件を持つ */
  patternId: string;
}

/**
 * 台帳 1 行の「業務」列。どの業務インスタンスが産んだ計算結果かを表し、
 * 詳細画面へ遷移するのに必要な最小情報だけを持つ。
 *
 * 表示名はここに持たない。業務名の訳は `pages.payrollCore` 配下に ja/en とも実在するため、
 * `-lib/i18nSelectors.ts` の selector で引く（データ層にロケール文字列を置かない）。
 */
export type RecordOperationRef = RecordOperationEventRef | RecordOperationPackageRef;

/** 業務パッケージは全パッケージが default パターン 1 件を持つ（payrollOpPackages seed）。 */
function packageRef(id: PayrollOpPackageId): RecordOperationPackageRef {
  return {
    id,
    kind: 'package',
    patternId: `${id}-pat-default`
  };
}
function eventRef(id: string, titleKey: string): RecordOperationEventRef {
  return {
    id,
    kind: 'event',
    titleKey
  };
}

/**
 * 給与計算 run → その run を回した業務。
 *
 * `SalaryRecord` は業務イベント／業務パッケージへの参照を持たず、逆に payrollGantt・
 * payrollOpPackages のモックも給与レコードを参照しないため、両者を結ぶキーがデータ上に
 * 存在しない。バックエンド接続時は run 側が業務インスタンス ID を保持するようになるため、
 * この定数は不要になる。
 *
 * キーは台帳の行 ID（`{payrollRunId}:{runType}`）。同じ payrollRunId でも定期給与と賞与は
 * 別の run なので、run 種別まで含めないと衝突する（JP 2026-06 が該当）。
 *
 * 業務イベント側の `eventId` / `titleKey` は payrollGantt seed の行と同じ組。
 * e2（managerOctober）に対応する給与レコードはモックに無いため未使用。
 */
export const OPERATION_BY_LEDGER_ROW_ID: Record<string, RecordOperationRef> = {
  'run-jp-2026-04:regular': packageRef('payrollCalculation'),
  'run-jp-2026-05:bonus': eventRef('e5', 'quarterlyBonus'),
  'run-jp-2026-05:regular': eventRef('e1', 'fulltimeOctober'),
  'run-jp-2026-06:bonus': eventRef('e4', 'bonus'),
  'run-jp-2026-06:regular': eventRef('e3', 'fulltimeNovember'),
  'run-uk-2026-04:regular': packageRef('payrollCalculation'),
  'run-uk-2026-05:regular': packageRef('payrollCalculation'),
  'run-uk-2026-06:regular': packageRef('payrollCalculation'),
  'run-us-2026-04:regular': packageRef('payrollCalculation'),
  // 臨時計算は給与計算サイクルの外で回すため、遡及計算パッケージ側に立つ
  'run-us-2026-05:off_cycle': packageRef('retroactiveCalculation'),
  'run-us-2026-05:regular': packageRef('payrollCalculation'),
  'run-us-2026-06:regular': packageRef('payrollCalculation')
};

/**
 * 対応づけを持たない run のフォールバック。業務列は空にできない（フィルタタブが
 * 業務イベント / 業務パッケージの 2 値で台帳を分割するため）ので、payroll-core の
 * 既定パッケージである給与計算に寄せる。
 */
export const FALLBACK_OPERATION: RecordOperationRef = packageRef('payrollCalculation');

/**
 * ステータスの進行度。run のステータスは「最も進んでいない明細」に合わせるため、
 * 比較できる数値で持つ。
 *
 * `cancelled` は進行の途中ではなく打ち切りなのでこの直線上に置かない。
 * `satisfies` で残り 7 値の網羅を強制する。`SalaryRecordStatus` にメンバーが増えたとき
 * ここがコンパイルエラーにならないと、新メンバーは順序不明のまま畳み込みから
 * 静かに消える（`on_hold` + `paid` の run が `paid` と出る）。
 */
export const RECORD_STATUS_RANK = {
  draft: 0,
  calculating: 1,
  calculated: 2,
  approving: 3,
  finalized: 4,
  delivered: 5,
  paid: 6
} satisfies Record<Exclude<SalaryRecordStatus, 'cancelled'>, number>;